import type React from "react";
import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PostChannelMoveOption } from "@/features/study-group-stream/types/postChannelMove";
import { buildPostCardMoveChannelSections } from "@/components/common/utils/postCardMoveChannelSections";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PostCardMoveChannelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isMovingChannel: boolean;
  movableChannels: PostChannelMoveOption[];
  selectedMoveChannelId?: string;
  onSelectedMoveChannelIdChange: (value: string) => void;
  onSubmit: () => Promise<void>;
}

export const PostCardMoveChannelDialog: React.FC<
  PostCardMoveChannelDialogProps
> = ({
  open,
  onOpenChange,
  isMovingChannel,
  movableChannels,
  selectedMoveChannelId,
  onSelectedMoveChannelIdChange,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const sectionedMovableChannels = useMemo(
    () => buildPostCardMoveChannelSections(movableChannels),
    [movableChannels],
  );

  const hasExtraSections =
    sectionedMovableChannels.adminOnly.length > 0 ||
    sectionedMovableChannels.archives.length > 0 ||
    sectionedMovableChannels.archivesAdminOnly.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {t("common.moveChannel", { defaultValue: "Move channel" })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <p className="text-sm text-gray-700">
            {t("common.selectDestinationChannel", {
              defaultValue: "Select destination channel",
            })}
          </p>
          <Select
            value={selectedMoveChannelId}
            onValueChange={onSelectedMoveChannelIdChange}
            disabled={isMovingChannel}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectLabel className="px-2 py-1 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                  {t("studyGroup.channels.sections.channels")}
                </SelectLabel>
                {sectionedMovableChannels.home && (
                  <SelectItem
                    key={sectionedMovableChannels.home.id}
                    value={sectionedMovableChannels.home.id}
                  >
                    {sectionedMovableChannels.home.name}
                  </SelectItem>
                )}
                {sectionedMovableChannels.channels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.name.replace(/^#\s*/, "")}
                  </SelectItem>
                ))}
              </SelectGroup>

              {sectionedMovableChannels.adminOnly.length > 0 && (
                <>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel className="px-2 py-1 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                      {t("studyGroup.channels.sections.adminOnly")}
                    </SelectLabel>
                    {sectionedMovableChannels.adminOnly.map((channel) => (
                      <SelectItem key={channel.id} value={channel.id}>
                        {channel.name.replace(/^#\s*/, "")}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}

              {sectionedMovableChannels.archives.length > 0 && (
                <>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel className="px-2 py-1 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                      {t("studyGroup.channels.sections.archives")}
                    </SelectLabel>
                    {sectionedMovableChannels.archives.map((channel) => (
                      <SelectItem key={channel.id} value={channel.id}>
                        {channel.name.replace(/^#\s*/, "")}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}

              {sectionedMovableChannels.archivesAdminOnly.length > 0 && (
                <>
                  <SelectSeparator />
                  <SelectGroup>
                    <SelectLabel className="px-2 py-1 text-xs uppercase tracking-[0.12em] text-muted-foreground">
                      {t("studyGroup.channels.sections.archivesAdminOnly")}
                    </SelectLabel>
                    {sectionedMovableChannels.archivesAdminOnly.map(
                      (channel) => (
                        <SelectItem key={channel.id} value={channel.id}>
                          {channel.name.replace(/^#\s*/, "")}
                        </SelectItem>
                      ),
                    )}
                  </SelectGroup>
                </>
              )}

              {!hasExtraSections &&
                sectionedMovableChannels.channels.length === 0 &&
                !sectionedMovableChannels.home &&
                movableChannels.map((channel) => (
                  <SelectItem key={channel.id} value={channel.id}>
                    {channel.kind === "home"
                      ? channel.name
                      : channel.name.replace(/^#\s*/, "")}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isMovingChannel}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="teal"
            onClick={() => void onSubmit()}
            disabled={isMovingChannel || !selectedMoveChannelId}
          >
            {isMovingChannel ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                {t("common.moving", { defaultValue: "Moving..." })}
              </>
            ) : (
              t("common.move", { defaultValue: "Move" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
